<template>
  <div class="container-xl customer-detail">
      <b-button size="sm" variant="outline-danger" @click="backToDashboard">
        <i class="fa fa-arrow-left mr-1"></i>
        {{ $t('label.back') }}
      </b-button>

    <!-- LOADING -->
    <div v-if="loading" class="text-muted py-5 text-center">
      Đang tải dữ liệu...
    </div>

    <!-- DETAIL -->
    <div v-else-if="customer">

      <!-- HEADER -->
      <div class="detail-header">
        <div>
          <h3 class="section-heading mb-2">Thông tin khách hàng</h3>
          <div class="info-row">
            <div class="info-block">
              <div class="info-block__row">
                <span class="info-block__label">Họ tên:</span>
                <span class="info-block__value">{{ customer.fullName }}</span>
              </div>
              <div class="info-block__row">
                <span class="info-block__label">SĐT:</span>
                <span class="info-block__value">{{ customer.phoneNumber }}</span>
              </div>
              <div class="info-block__row">
                <span class="info-block__label">Địa chỉ:</span>
                <span class="info-block__value">{{ customer.address }}</span>
              </div>
              <div class="info-block__row">
                <span class="info-block__label">Trạng thái cuộc gọi:</span>
                <span class="info-block__value">{{ assignmentStatusText }}</span>
              </div>
              <div class="info-block__row">
                <span class="info-block__label">Trạng thái đơn hàng:</span>

                <span class="info-block__value" :class="isOrdered ? 'text-success' : 'text-secondary'">
                  {{ isOrdered ? 'Đã đặt đơn' : 'Chưa đặt đơn' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <b-button class="btn btn-info" @click="openCreateNote">
          + Thêm ghi chú
        </b-button>
      </div>

      <!-- SECTION: Ghi chú khách hàng -->
      <h3 class="section-heading mt-5">Ghi chú khách hàng</h3>

      <div v-if="loadingNotes" class="text-muted py-3">Đang tải ghi chú...</div>
      <div v-else>
        <div class="table-wrapper">
          <b-table bordered hover head-variant="dark" :items="notes" :fields="noteFields"
            empty-text="Chưa có ghi chú nào được thêm cho khách hàng này" show-empty>
            <template #cell(stt)="data">
              {{ data.index + 1 + (notePage - 1) * notePerPage }}
            </template>

            <template #cell(createdAt)="data">
              <small class="text-muted">{{ formatDate(data.item.createdAt) }}</small>
            </template>

            <template #cell(note)="data">
              <div class="note-cell">{{ data.item.note }}</div>
            </template>

            <template #cell(action)="data">za
              <div class="action-icons">
                <b-button size="sm" variant="link" class="icon-btn text-primary" title="Chỉnh sửa"
                  @click="openEditNote(data.item)">
                  <i class="fa fa-edit"></i>
                </b-button>

                <b-button size="sm" variant="link" class="icon-btn text-danger" title="Xoá"
                  @click="deleteNote(data.item)">
                  <i class="fa fa-trash"></i>
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <div v-if="noteTotal > 0" class="d-flex justify-content-between mt-3 flex-wrap">
          <small class="text-muted">
            Hiển thị
            {{ (notePage - 1) * notePerPage + 1 }}
            –
            {{ Math.min(notePage * notePerPage, noteTotal) }}
            /
            {{ noteTotal }}
            bản ghi
          </small>

          <div class="d-flex">
            <b-form-select size="sm" v-model="noteSelectedPerPage" :options="optionsPages" class="mr-2"
              style="max-width: 100px" @change="changeNotePerPage" />
            <b-pagination v-model="notePage" :total-rows="noteTotal" :per-page="notePerPage" size="sm"
              @change="fetchNotes" />
          </div>
        </div>
      </div>

      <!-- SECTION: Cập nhật trạng thái -->
      <h3 class="section-heading mt-5">Cập nhật trạng thái</h3>
      <div class="update-status-row">
        <b-form-group label="Trạng thái mới" class="update-status-row__field">
          <b-form-select v-model="newStatus" :options="statusOptions" :disabled="updatingStatus" />
        </b-form-group>

      <b-form-group label="Ghi chú (tuỳ chọn)" class="update-status-row__field">
        <b-form-input
          v-model="statusNote"
          :disabled="updatingStatus"
          placeholder="Nhập ghi chú..."
        />
      </b-form-group>

        <b-button variant="primary" class="update-status-row__btn" style="cursor: pointer"
          :disabled="!canUpdateStatus || updatingStatus" @click="updateStatus">
          {{ updatingStatus ? 'Đang cập nhật...' : 'Cập nhật trạng thái' }}
        </b-button>
      </div>

      <!-- SECTION: Lịch sử thay đổi trạng thái -->
      <h3 class="section-heading mt-5">Lịch sử thay đổi trạng thái</h3>

      <div v-if="loadingStatusHistory" class="text-muted py-3">
        Đang tải lịch sử...
      </div>
      <div v-else>
        <div class="table-wrapper">
          <b-table bordered hover head-variant="dark" :items="statusHistories" :fields="statusHistoryFields"
            empty-text="Chưa có lịch sử thay đổi trạng thái nào được ghi lại" show-empty>
            <template #cell(stt)="data">
              {{ data.index + 1 + (statusHistoryPage - 1) * statusHistoryPerPage }}
            </template>

            <template #cell(createdAt)="data">
              <small class="text-muted">{{ formatDate(data.item.createdAt) }}</small>
            </template>

            <template #cell(oldStatus)="data">
              <span class="text-muted">{{ statusText(data.item.oldStatus) }}</span>
            </template>

           <template #cell(newStatus)="data">
            <span
              class="status-item"
              :class="statusClass(data.item.newStatus)"
            >
              {{ statusText(data.item.newStatus) }}
            </span>
          </template>

            <template #cell(note)="data">
              <div class="note-cell">
                <span v-if="data.item.note">{{ data.item.note }}</span>
                <span v-else class="text-muted">—</span>
              </div>
            </template>
          </b-table>
        </div>

        <div v-if="statusHistoryTotal > 0" class="d-flex justify-content-between mt-3 flex-wrap">
          <small class="text-muted">
            Hiển thị
            {{ (statusHistoryPage - 1) * statusHistoryPerPage + 1 }}
            –
            {{ Math.min(statusHistoryPage * statusHistoryPerPage, statusHistoryTotal) }}
            /
            {{ statusHistoryTotal }}
            bản ghi
          </small>

          <div class="d-flex">
            <b-form-select size="sm" v-model="statusHistorySelectedPerPage" :options="optionsPages" class="mr-2"
              style="max-width: 100px" @change="changeStatusHistoryPerPage" />
            <b-pagination v-model="statusHistoryPage" :total-rows="statusHistoryTotal" :per-page="statusHistoryPerPage"
              size="sm" @change="fetchStatusHistory" />
          </div>
        </div>
      </div>

    </div>

    <!-- NO PERMISSION -->
    <div v-else class="text-danger py-5 text-center">
      Bạn không có quyền xem khách hàng này hoặc dữ liệu không tồn tại
    </div>

    <!-- MODAL -->
    <b-modal id="note-modal" title="Ghi chú khách hàng" @ok="saveNote" centered>
      <b-form-textarea v-model="noteContent" rows="4" placeholder="Nhập ghi chú..." />
    </b-modal>

  </div>
</template>

<script>
import apiClient from '@/axios'

const STATUS_MAP = {
  1: 'Đồng ý',
  2: 'Từ chối',
  3: 'Không nhấc máy',
  4: 'Thuê bao',
  5: 'Cần tư vấn thêm'
}

export default {
  data() {
    return {
      loading: false,
      customer: null,

      // notes
      notes: [],
      loadingNotes: false,
      noteContent: '',
      editingNote: null,
      notePage: 1,
      notePerPage: 10,
      noteSelectedPerPage: 10,
      noteTotal: 0,

      // update status
      updatingStatus: false,
      newStatus: null,
      statusNote: '',

      // status history
      statusHistories: [],
      loadingStatusHistory: false,
      statusHistoryPage: 1,
      statusHistoryPerPage: 10,
      statusHistorySelectedPerPage: 10,
      statusHistoryTotal: 0,

      // shared
      optionsPages: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      statusClass(status) {
        return {
          accepted: status === 1,
          reject: status === 2,
          noAnswer: status === 3,
          notCatch: status === 4,
          consultation: status === 5
      }
}
    }
  },

  computed: {
    assignmentStatusText() {
      return STATUS_MAP[this.customer?.assignmentStatus] || '—'
    },

    isOrdered() {
      return !!this.customer?.customerOrderId
    },

    statusOptions() {
        return [
        { value: null, text: 'Chọn trạng thái' },
        { value: 1, text: 'Đồng ý' },
        { value: 2, text: 'Từ chối' },
        { value: 3, text: 'Không nhấc máy' },
        { value: 4, text: 'Thuê bao' },
        { value: 5, text: 'Cần tư vấn thêm' }
      ]
    },

    noteFields() {
      return [
        { key: 'stt', label: '#', thStyle: { width: '50px' }, tdClass: 'text-center' },
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'note', label: 'Nội dung ghi chú' },
        { key: 'action', label: '', thStyle: { width: '100px' } }
      ]
    },

    statusHistoryFields() {
      return [
        { key: 'stt', label: '#', thStyle: { width: '50px' }, tdClass: 'text-center' },
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'saleName', label: 'Sale' },
        { key: 'oldStatus', label: 'Trạng thái cũ' },
        { key: 'newStatus', label: 'Trạng thái mới' },
        { key: 'note', label: 'Ghi chú' }
      ]
    },

    canUpdateStatus() {
      if (!this.newStatus) return false
      if (![1, 2, 3, 4, 5].includes(this.newStatus)) return false
      return true
    }
  },

  watch: {
    customer(val) {
      if (val?.id) {
        this.fetchNotes()
        this.fetchStatusHistory()
      }
    }
  },

  mounted() {
    this.fetchDetail()
  },

  methods: {
    statusText(status) {
      return STATUS_MAP[status] || '—'
    },

   formatDate(value) {
      if (!value) return ''
      return this.$moment
        ? this.$moment(value).format('DD/MM/YYYY HH:mm')
        : new Date(value).toLocaleString()
    },

    async fetchDetail() {
      const id = this.$route.params.id
      if (!id) return

      this.loading = true
      try {
        const res = await apiClient.get(`/api/v1/customer/detail/${id}`)
        this.customer = this.mapCustomerDetail(res.data)
      } catch (e) {
        this.customer = null
      } finally {
        this.loading = false
      }
    },

    mapCustomerDetail(data) {
      return {
        id: data.id,
        idHash: data.idHash,
        fullName: data.full_name,
        phoneNumber: data.phone_number,
        address: data.address,
        assignmentId: data.assignment_id,
        saleId: data.sale_id,
        assignmentStatus: data.assignment_status,
        assignmentNote: data.assignment_note,
        actionDatetime: data.action_datetime,
        customerOrderId: data.customer_order_id,
        status: data.status
      }
    },

    async fetchNotes() {
      if (!this.customer?.id) return

      this.loadingNotes = true
      try {
        const res = await apiClient.get('/api/v1/customer-notes', {
          params: {
            customerId: this.customer.id,
            perPage: this.notePerPage,
            currentPage: this.notePage
          }
        })

        const meta = res.data.metadata
        this.notes = meta.items.map(i => ({
          id: i.id,
          note: i.note,
          createdAt: i.created_at
        }))
        this.noteTotal = meta.total
      } finally {
        this.loadingNotes = false
      }
    },

    openCreateNote() {
      this.editingNote = null
      this.noteContent = ''
      this.$bvModal.show('note-modal')
    },

    openEditNote(note) {
      this.editingNote = note
      this.noteContent = note.note
      this.$bvModal.show('note-modal')
    },

    async saveNote() {
      if (!this.noteContent) return

      try {
        if (this.editingNote) {
          await apiClient.put('/api/v1/customer-notes', {
            id: this.editingNote.id,
            note: this.noteContent
          })

          this.$bvToast.toast('Cập nhật ghi chú thành công!', {
            title: 'Thành công',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-right'
          })
        } else {
          await apiClient.post('/api/v1/customer-notes', {
            customerId: this.customer.id,
            note: this.noteContent
          })

          this.$bvToast.toast('Thêm ghi chú thành công!', {
            title: 'Thành công',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-right'
          })
        }

        this.noteContent = ''
        this.editingNote = null
        await this.fetchNotes()

      } catch (e) {
        const message =
          e?.response?.data?.message || 'Không thể lưu ghi chú. Vui lòng thử lại'

        this.$bvToast.toast(message, {
          title: 'Thất bại',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right'
        })
      }
    },

    async deleteNote(note) {
      try {
        await apiClient.delete(`/api/v1/customer-notes/${note.id}`)

        this.$bvToast.toast('Xoá ghi chú thành công!', {
          title: 'Thành công',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-right'
        })

        await this.fetchNotes()
      } catch (e) {
        const message =
          e?.response?.data?.message || 'Không thể xoá ghi chú'

        this.$bvToast.toast(message, {
          title: 'Thất bại',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right'
        })
      }
    },

    changeNotePerPage() {
      this.notePage = 1
      this.notePerPage = this.noteSelectedPerPage
      this.fetchNotes()
    },

    changeStatusHistoryPerPage() {
      this.statusHistoryPage = 1
      this.statusHistoryPerPage = this.statusHistorySelectedPerPage
      this.fetchStatusHistory()
    },

    async updateStatus() {
      if (!this.canUpdateStatus) return

      this.updatingStatus = true
      try {
        await apiClient.post('/api/v1/sale/customer-status', {
          customerId: this.customer.id,
          newStatus: this.newStatus,
          note: this.statusNote
        })

        this.$bvToast.toast('Cập nhật trạng thái thành công!', {
          title: 'Thành công',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-right'
        })

        await this.fetchDetail()
        await this.fetchStatusHistory()

        this.newStatus = null
        this.statusNote = ''
      } catch (e) {
        const message =
          e?.response?.data?.message || 'Cập nhật trạng thái thất bại'

        this.$bvToast.toast(message, {
          title: 'Thất bại',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right'
        })
      } finally {
        this.updatingStatus = false
      }
    },

    async fetchStatusHistory() {
      if (!this.customer?.id) return

      this.loadingStatusHistory = true
      try {
        const res = await apiClient.get('/api/v1/customer-status-history', {
          params: {
            customerId: this.customer.id,
            perPage: this.statusHistoryPerPage,
            currentPage: this.statusHistoryPage
          }
        })

        const meta = res.data.metadata
        this.statusHistories = meta.items.map(i => ({
          id: i.id,
          saleName: i.sale_name,
          oldStatus: i.old_status,
          newStatus: i.new_status,
          note: i.note,
          createdAt: i.created_at
        }))
        this.statusHistoryTotal = meta.total
      } finally {
        this.loadingStatusHistory = false
      }
    },
    backToDashboard() {
      this.$router.replace('/sale/history')
    },
  }
}
</script>

<style scoped>
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 0 10px;
}

.section-heading {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: none;
  margin-bottom: 12px;
  padding-bottom: 0;
}

.info-row {
  display: flex;
  gap: 60px;
  flex-wrap: wrap;
}

.info-block__row {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.9rem;
  color: #333;
}

.info-block__label {
  font-weight: 600;
  color: #555;
  min-width: 80px;
}

.info-block__value {
  color: #1a1a1a;
}

.table-wrapper {
  overflow-x: hidden;
}

.table-wrapper table {
  width: 100%;
  table-layout: fixed;
}

.table-wrapper th,
.table-wrapper td {
  word-break: break-word;
}

.action-icons {
  display: flex;
  gap: 6px;
}

.icon-btn {
  padding: 4px;
  border: none !important;
  background: transparent !important;
}

.icon-btn:hover {
  opacity: 0.75;
}

.note-cell {
  max-width: 250px;
  word-wrap: break-word;
  white-space: normal;
}

.update-status-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.update-status-row__field {
  margin-bottom: 0;
  min-width: 200px;
}

.update-status-row__btn {
  margin-bottom: 4px;
  padding: 6px 20px;
}

.status-item.accepted {
  color: #28a745;
}

.status-item.reject {
  color: #dc3545;
}

.status-item.noAnswer {
  color: #ffc107;
}

.status-item.notCatch {
  color: #6c757d;
}

.status-item.consultation {
  color: #17a2b8;
}
</style>
